import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = "/scube-logo.png";
const BRAND_NAME = "S Cube AI Consulting & Solutions";

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'About', href: '/about' },
  { label: 'Insights', href: '/insights' },
  { label: 'Contact', href: '/contact' },
];

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header
        data-testid="site-header"
        className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-white/20 shadow-sm"
        style={{ WebkitBackdropFilter: 'blur(24px)', backdropFilter: 'blur(24px)' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20 md:h-24">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 md:gap-4 min-w-0">
            <img src={LOGO_URL} alt={BRAND_NAME} className="h-14 md:h-20 w-auto flex-shrink-0" />
            <span className="font-heading text-navy block leading-tight min-w-0">
              <span className="block font-bold text-lg sm:text-2xl">S Cube</span>
              <span className="block text-[9px] sm:text-xs font-semibold uppercase tracking-[0.12em] sm:tracking-[0.16em] text-navy/55 max-w-[170px] sm:max-w-none truncate">
                AI Consulting & Solutions
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map(link => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`text-sm font-medium transition-colors hover:text-teal ${
                  location.pathname === link.href ? 'text-teal' : 'text-navy/70'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <Link to="/contact">
              <Button data-testid="header-cta" className="bg-navy text-white hover:bg-navy/90 rounded-full px-6">
                Get in Touch <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 text-navy"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div data-testid="mobile-nav" className="md:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-3">
            {navLinks.map(link => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className={`block text-sm font-medium py-2 ${
                  location.pathname === link.href ? 'text-teal' : 'text-navy/70'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/contact" onClick={() => setMobileOpen(false)}>
              <Button className="w-full bg-navy text-white hover:bg-navy/90 rounded-full mt-2">
                Get in Touch
              </Button>
            </Link>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1 pt-20 md:pt-24">
        {children}
      </main>

      {/* Footer */}
      <footer data-testid="site-footer" className="bg-navy text-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
            <div className="md:col-span-5">
              <h2 className="font-heading text-4xl md:text-5xl font-medium mb-6">
                Let's Talk
              </h2>
              <p className="text-white/60 text-base leading-relaxed max-w-md mb-8">
                Ready to transform your business with AI? Our team of experts is here to help you navigate the future.
              </p>
              <Link to="/contact">
                <Button data-testid="footer-cta" className="bg-teal text-white hover:bg-teal/90 rounded-full px-8 py-3 text-base">
                  Schedule a Consultation <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="md:col-span-3 md:col-start-7">
              <h4 className="font-heading text-sm uppercase tracking-[0.2em] font-bold text-white/40 mb-6">Navigation</h4>
              <div className="space-y-3">
                {navLinks.map(link => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="block text-white/60 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="md:col-span-3">
              <h4 className="font-heading text-sm uppercase tracking-[0.2em] font-bold text-white/40 mb-6">Industries</h4>
              <div className="space-y-3">
                {['Retail', 'Manufacturing', 'Energy & Utilities', 'Telecom'].map(ind => (
                  <Link
                    key={ind}
                    to="/services"
                    className="block text-white/60 hover:text-white transition-colors text-sm"
                  >
                    {ind}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt={BRAND_NAME} className="h-12 w-auto" />
              <span className="font-heading text-base text-white/60">{BRAND_NAME}</span>
            </div>
            <div className="text-white/40 text-xs text-center md:text-right">
              &copy; 2025 {BRAND_NAME} a unit of 3SAGE AI CONSULTING &amp; SOLUTIONS LLP |{' '}
              <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
